import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getAbsoluteCoverUrl, getPlaceholderCover } from '../../utils/imageUtils';
import { renderAuthor } from '../../utils/authorDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContinueReading = ({ user }) => {
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProgress = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/reading-progress`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProgress(response.data);
      } catch (error) {
        console.error('Failed to load reading progress:', error);
      }
      setLoading(false);
    };

    loadProgress();
  }, [user]);

  if (!user || loading) return null;
  if (progress.length === 0) return null;
  return (
    <section className="mb-8 sm:mb-12" data-testid="continue-reading-section">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-dark-text flex items-center gap-2">
          <span className="text-2xl">📖</span>
          Continue Reading
        </h2>
        <Link
          to="/library"
          className="text-dark-accent hover:text-dark-accent-hover text-sm font-semibold"
          data-testid="continue-reading-view-all"
        >
          View all →
        </Link>
      </div>

      <div className="overflow-x-auto no-scrollbar">
        <div className="flex gap-4 sm:gap-6 pb-4">
          {progress.map((item) => {
            const total = Math.max(1, Number(item.total_chapters) || 1);
            const current = Math.min(total, Math.max(0, Number(item.chapter_number) || 0));
            const pct = Math.round((current / total) * 100);
            const authorLabel = renderAuthor(item);
            const accessibleLabel = authorLabel
              ? `${item.story_title} by ${authorLabel} — chapter ${current} of ${total}`
              : `${item.story_title} — chapter ${current} of ${total}`;
            return (
              <Link
                key={item.story_id}
                to={`/book/${item.story_id}/read/${item.chapter_id}`}
                className="flex-shrink-0 w-[112px] sm:w-36 md:w-40 cursor-pointer group"
                data-testid={`continue-reading-${item.story_id}`}
                title={accessibleLabel}
                aria-label={accessibleLabel}
              >
                <div className="relative overflow-hidden rounded-lg transition-transform group-hover:scale-105 aspect-[2/3] bg-dark-card">
                  <img
                    src={getAbsoluteCoverUrl(item.story_cover)}
                    alt={item.story_title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => { e.target.src = getPlaceholderCover(); }}
                  />

                  {/* Chapter count badge */}
                  <span
                    className="absolute top-1 right-1 px-1 py-0 rounded bg-black/70 text-white text-[7px] sm:text-[9px] font-semibold leading-tight backdrop-blur-sm"
                    data-testid={`continue-reading-chapter-${item.story_id}`}
                  >
                    Ch {current}/{total}
                  </span>

                  {/* Progress bar overlay at bottom of cover */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-1.5 bg-black/50"
                    aria-label={`Reading progress ${pct}%`}
                    data-testid={`continue-reading-progress-${item.story_id}`}
                  >
                    <div
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ContinueReading;
